"use client";

import React, { useCallback, useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";

export type ListingAgentMultiSelectItem = {
  agentId: string;
  agentName: string;
  propertyCount: number;
};

export type MultiSelectListingAgentsProps = {
  options: ListingAgentMultiSelectItem[];
  selectedAgentIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
};

type MultiSelectTriggerSummaryProps = {
  selectedAgents: ListingAgentMultiSelectItem[];
  placeholder: string;
  countFormatter: Intl.NumberFormat;
};

function MultiSelectTriggerSummary({
  selectedAgents,
  placeholder,
  countFormatter,
}: MultiSelectTriggerSummaryProps): React.ReactNode {
  const n = selectedAgents.length;

  if (n === 0) {
    return <span className="text-sm font-normal">{placeholder}</span>;
  }

  if (n === 1) {
    const agent = selectedAgents[0];
    return (
      <span className="text-sm font-medium text-foreground">
        {agent.agentName}
        <span className="ml-1 text-xs font-normal tabular-nums text-muted-foreground">
          ({countFormatter.format(agent.propertyCount)})
        </span>
      </span>
    );
  }

  if (n <= 2) {
    return selectedAgents.map((agent) => (
      <Badge
        key={agent.agentId}
        variant="secondary"
        className="select-none text-xs font-medium"
      >
        {agent.agentName}
      </Badge>
    ));
  }

  return (
    <span className="text-sm font-medium tabular-nums text-foreground">
      {n} agents selected
    </span>
  );
}

type ListingAgentCommandRowProps = {
  agent: ListingAgentMultiSelectItem;
  isSelected: boolean;
  countFormatter: Intl.NumberFormat;
  onToggle: () => void;
};

function ListingAgentCommandRow({
  agent,
  isSelected,
  countFormatter,
  onToggle,
}: ListingAgentCommandRowProps): React.ReactElement {
  const countLabel = `(${countFormatter.format(agent.propertyCount)} properties)`;
  const commandValue = `${agent.agentName} ${agent.agentId} ${String(agent.propertyCount)}`;

  return (
    <CommandItem
      value={commandValue}
      onSelect={onToggle}
      className="cursor-pointer"
    >
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary",
            isSelected
              ? "bg-primary text-primary-foreground"
              : "opacity-50 [&_svg]:invisible",
          )}
        >
          <Check className="h-3 w-3" />
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-sm font-medium text-foreground">
            {agent.agentName}
          </span>
          <span className="ml-1.5 text-xs font-normal tabular-nums text-muted-foreground">
            {countLabel}
          </span>
        </div>
      </div>
    </CommandItem>
  );
}

/**
 * Searchable multi-select for property listing agents (staff dashboard filters).
 */
export function MultiSelectListingAgents({
  options,
  selectedAgentIds = [],
  onSelectionChange,
  placeholder = "Filter by listing agents",
  searchPlaceholder = "Search agents",
  emptyText = "No agents match that search.",
  disabled = false,
  className,
}: MultiSelectListingAgentsProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const idBase = React.useId();
  const listboxPanelId = `${idBase}-listbox-panel`;
  const countFormatter = useMemo(() => new Intl.NumberFormat(), []);

  // ---------------------------------------------------------------------------
  // dismiss layer (sheet + popover resizeobserver workaround — inline panel)
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      if (root === null) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (root.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const selectedAgents = useMemo(() => {
    const byId = new Map<string, ListingAgentMultiSelectItem>();
    for (const option of options) {
      byId.set(option.agentId, option);
    }
    const ordered: ListingAgentMultiSelectItem[] = [];
    for (const id of selectedAgentIds) {
      const agent = byId.get(id);
      if (agent !== undefined) {
        ordered.push(agent);
      }
    }
    return ordered;
  }, [options, selectedAgentIds]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === "") {
      return options;
    }
    return options.filter((agent) => {
      const name = agent.agentName.toLowerCase();
      const id = agent.agentId.toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [options, search]);

  const handleSelect = useCallback(
    (agentId: string) => {
      const newSelection = selectedAgentIds.includes(agentId)
        ? selectedAgentIds.filter((id) => id !== agentId)
        : [...selectedAgentIds, agentId];
      onSelectionChange(newSelection);
    },
    [selectedAgentIds, onSelectionChange],
  );

  const handleRemove = useCallback(
    (agentId: string) => {
      onSelectionChange(selectedAgentIds.filter((id) => id !== agentId));
    },
    [selectedAgentIds, onSelectionChange],
  );

  const handleClearAll = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  return (
    <div ref={rootRef} className={cn("relative w-full", className)}>
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listboxPanelId : undefined}
        onClick={() => {
          if (disabled) {
            return;
          }
          setOpen((prev) => !prev);
        }}
        className={cn(
          "h-auto min-h-9 w-full justify-between py-2 text-base active:scale-95 transition-all duration-150 ease-out cursor-pointer",
          selectedAgents.length > 0
            ? "text-foreground"
            : "text-muted-foreground",
          disabled && "cursor-not-allowed opacity-50 active:scale-100",
        )}
        disabled={disabled}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-left">
          <MultiSelectTriggerSummary
            selectedAgents={selectedAgents}
            placeholder={placeholder}
            countFormatter={countFormatter}
          />
        </div>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open ? (
        <div
          id={listboxPanelId}
          className={cn(
            "absolute left-0 right-0 top-full z-[70] mt-1 max-h-[min(24rem,calc(100vh-6rem))] w-full min-w-0 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md outline-hidden",
            "origin-top animate-in fade-in-0 zoom-in-95 duration-150",
          )}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
              disabled={disabled}
              className="min-h-9 text-base"
            />
            <CommandList className="max-h-60">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {selectedAgents.length > 0 ? (
                  <div className="flex items-center justify-between border-b px-2 py-2">
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {selectedAgents.length} selected
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleClearAll}
                      className="h-auto select-none p-1 text-xs active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                    >
                      Clear all
                    </Button>
                  </div>
                ) : null}
                {filteredOptions.map((agent) => (
                  <ListingAgentCommandRow
                    key={agent.agentId}
                    agent={agent}
                    isSelected={selectedAgentIds.includes(agent.agentId)}
                    countFormatter={countFormatter}
                    onToggle={() => handleSelect(agent.agentId)}
                  />
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      ) : null}

      {selectedAgents.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedAgents.map((agent) => (
            <Badge
              key={agent.agentId}
              variant="secondary"
              className="flex select-none items-center gap-1 text-xs font-medium"
            >
              {agent.agentName}
              <button
                type="button"
                onClick={() => handleRemove(agent.agentId)}
                className="ml-0.5 rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                disabled={disabled}
                aria-label={`Remove ${agent.agentName}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}
